import CameraService, { CameraFacingMode } from '../../../helpers/classes/CameraService';
import EventService from '../../../helpers/classes/EventService';
import TemplateService from '../../../helpers/classes/TemplateService';
import RetinaFactory from '../../../retina/factory';
import { CortixResponseAction, ICortixResponse } from '../../../retina/types';
import DataService, { DataStoreType } from '../../classes/DataService';
import { JourneyContainer } from '../../classes/JourneyContainer';
import JourneyManager from '../../classes/JourneyManager';
import { JourneyEvent } from '../../enums/JourneyEvent';
import { Templates } from '../../enums/Templates';
import { UploadResponse } from '../../interfaces/UploadResponse';
import { IIdScanData } from '../../interfaces/DataFormats';
import { IExtendedDocument } from '../../interfaces/Document';
import { removeNullOrUndefined } from '../../../helpers/functions/object';
import { HintType } from '../../enums/Camera';
import * as lottie from 'lottie-web/build/player/lottie';
import LanguageService from '../../../helpers/classes/LanguageService';
import RenderingService from '../../../helpers/classes/RenderingService';
import { TranslationKey } from '../../enums/Translation';
import { IUnsubscribable } from '../../interfaces/Unsubscribable';
import { AnimationService } from '../../../helpers/classes/AnimationService';

export class LivenessStep {
	public static render(this: JourneyContainer) {
		return CameraService.getVideoCameras()
			.then((cameras) => cameras.map((camera) => ({
				name: camera.label,
				value: camera.deviceId,
			})))
			.then((cameras) => {
				LivenessStep.cameras = cameras;
				return TemplateService.renderToContainer(this._container, Templates.Liveness, { cameras })
			})
			.then((container) => this.changeCameraStream(undefined, undefined, !this._config.hideLogo, HintType.NONE, undefined, CameraFacingMode.FRONT).then(() => container))
			.then((container) => {
				return RetinaFactory
					.getNewService(
						this._journey!.id,
						container,
						JourneyManager.getToken()!,
						{
							autoRun: false,
							cortexApiController: 'LivenessStreamingSocket',
							cortexApiPath: this._config.backendUrl.split('//')[1] + '/idscanEnterpriseSVC/',
							frameSubmissionDelay: 0.001,
							grayscale: true,
							imageQuality: 75,
						}
					)
					.then((mediaInstance) => {
						TemplateService.getElements(this._container, {
							cameraSelect: '[data-jcs-element="camera__select"]'
						})
							.then(({ cameraSelect }) => {
								if (cameraSelect instanceof HTMLSelectElement) {
									RenderingService.updateSelect(cameraSelect, LivenessStep.cameras);
									if (LivenessStep.cameras && LivenessStep.cameras.length > 1) {
										RenderingService.showObject(cameraSelect);
									} else {
										RenderingService.hideObject(cameraSelect);
									}
								}
							});
						mediaInstance.emitter.on(CortixResponseAction.StopCapturing, (msg: ICortixResponse) => {

							const data = JourneyManager.caseDic(msg.workflowState);

							DataService.getDataStore<IIdScanData>(DataStoreType.ID_SCAN)
								.update(LivenessStep.parseResponseData(data));

							this._journey!.update(data)
								.then(() => mediaInstance.terminate())
								.then(() => {
									if (LivenessStep.livenessAnimation !== undefined && LivenessStep.livenessAnimation !== null) {
										LivenessStep.livenessAnimation.stop();
										LivenessStep.livenessAnimation.destroy();
										LivenessStep.livenessAnimation = undefined;
									}
									if (LivenessStep.cameraPoseSubscription !== undefined) {
										LivenessStep.cameraPoseSubscription.unsubscribe();
										LivenessStep.cameraPoseSubscription = undefined;
									}
								})
								.then(() => this.evaluate());
						});

						mediaInstance.emitter.on(CortixResponseAction.FinishLivenessCapturing, (msg: ICortixResponse) => {
							EventService.publish(JourneyEvent.LIVENESS_PROCESSING, { step: Templates.Liveness });
						});

						mediaInstance.emitter.on(CortixResponseAction.Error, () => {
							mediaInstance.terminate()
								.then(() => {
									if (LivenessStep.livenessAnimation !== undefined && LivenessStep.livenessAnimation !== null) {
										LivenessStep.livenessAnimation.stop();
										LivenessStep.livenessAnimation.destroy();
										LivenessStep.livenessAnimation = undefined;
									}
									if (LivenessStep.cameraPoseSubscription !== undefined) {
										LivenessStep.cameraPoseSubscription.unsubscribe();
										LivenessStep.cameraPoseSubscription = undefined;
									}
								})
								.then(() => this.evaluate());
						});

						mediaInstance.emitter.on(CortixResponseAction.Close, ({ code }: CloseEvent) => {
							EventService.publish(JourneyEvent.LIVENESS_FINISHED, { step: Templates.Liveness });
							if (code !== 1000) {
								return mediaInstance.terminate()
									.then(() => {
										if (LivenessStep.livenessAnimation !== undefined && LivenessStep.livenessAnimation !== null) {
											LivenessStep.livenessAnimation.stop();
											LivenessStep.livenessAnimation.destroy();
											LivenessStep.livenessAnimation = undefined;
										}
										if (LivenessStep.cameraPoseSubscription !== undefined) {
											LivenessStep.cameraPoseSubscription.unsubscribe();
											LivenessStep.cameraPoseSubscription = undefined;
										}
									})
									.then(() => this.evaluate());
							}
						});
					});
			})
			.then(() => TemplateService.getElements(this._container, {
				cameraViewfinder: '[data-jcs-element="camera__viewfinder"]',
				hintAnimationContainer: '[data-jcs-element="animation__viewfinder"]',
				animationContainer: '[data-jcs-element="animation__container"]',
				animationTitle: '[data-jcs-element="animation__title"]',
				animationText: '[data-jcs-element="animation__text"]',
				livenessStart: '[data-jcs-element="retina__start"]',
				startContainerOverlay: '[data-jcs-element="liveness__start__button__container__overlay"]',
				startContainerButton: '[data-jcs-element="liveness__start__button__container__button"]',
			}))
			.then(({ cameraViewfinder, hintAnimationContainer, animationContainer, animationTitle, animationText, livenessStart, startContainerOverlay, startContainerButton }) => {
				if (animationContainer instanceof HTMLDivElement && animationTitle instanceof HTMLParagraphElement && animationText instanceof HTMLParagraphElement) {
					LivenessStep.cameraPoseSubscription = EventService.subscribe(JourneyEvent.CAMERA_POSE, (actionData: any) => {
						LivenessStep.updateAnimation.call(this, animationContainer, animationTitle, animationText, actionData);
					});
				}
				if (startContainerOverlay instanceof HTMLDivElement && startContainerButton instanceof HTMLDivElement && hintAnimationContainer instanceof HTMLDivElement && cameraViewfinder instanceof HTMLCanvasElement) {
					EventService.subscribe(
						JourneyEvent.CAMERA_CANVAS_RESIZE,
						() => {
							LivenessStep.handleCameraSelectedChange.call(this, hintAnimationContainer, startContainerOverlay, startContainerButton, cameraViewfinder);
							RenderingService.showObject(startContainerOverlay);
							RenderingService.showObject(startContainerButton);
						}
					);
				}
				if (livenessStart instanceof HTMLElement) {
					livenessStart.addEventListener('click', () => {
						RenderingService.hideObject(startContainerOverlay);
						RenderingService.hideObject(startContainerButton);
						RenderingService.showObject(hintAnimationContainer, 'flex');
					});
				}
			})
			.then(() => EventService.publish(JourneyEvent.DISPLAY_STEP, { step: Templates.Liveness }));
	}

	private static livenessAnimation: lottie.AnimationItem | undefined;
	private static cameraPoseSubscription: IUnsubscribable | undefined;
	private static cameras: { name: string; value: string; }[];

	private static handleCameraSelectedChange(this: JourneyContainer, animationContainer: HTMLDivElement, startContainerOverlay: HTMLDivElement, startContainerButton: HTMLDivElement, canvasContainer: HTMLCanvasElement) {
		try {
			animationContainer.style.height = `${canvasContainer.clientHeight}px`;
			animationContainer.style.width = `${canvasContainer.clientWidth}px`;
			startContainerOverlay.style.height = `${canvasContainer.clientHeight}px`;
			startContainerOverlay.style.width = `${canvasContainer.clientWidth}px`;
			startContainerButton.style.height = `${canvasContainer.clientHeight}px`;
			startContainerButton.style.width = `${canvasContainer.clientWidth}px`;
		} catch (error) {
			console.log(error);
		}
	}

	private static updateAnimation(this: JourneyContainer, animationContainer: HTMLDivElement, animationTitle: HTMLParagraphElement, animationText: HTMLParagraphElement, poseAction: any) {
		try {
			console.log(poseAction);
			if (LivenessStep.livenessAnimation !== undefined && LivenessStep.livenessAnimation !== null) {
				LivenessStep.livenessAnimation.stop();
				LivenessStep.livenessAnimation.destroy();
				LivenessStep.livenessAnimation = undefined;
			}
			switch (poseAction.pose) {
				case 'Look Straight':
					RenderingService.setText(animationTitle, LanguageService.getTranslation(TranslationKey.LIVENESS_ACTION_LOOK_STRAIGHT_TITLE));
					RenderingService.setText(animationText, LanguageService.getTranslation(TranslationKey.LIVENESS_ACTION_LOOK_STRAIGHT_TEXT));
					break;
				case 'Smile':
					RenderingService.setText(animationContainer, '');
					LivenessStep.livenessAnimation = AnimationService.CreateAnimation(animationContainer, this.assetsFolder + '/animations/smile.json', true, true, {});
					RenderingService.setText(animationTitle, LanguageService.getTranslation(TranslationKey.LIVENESS_ACTION_SMILE_TITLE));
					RenderingService.setText(animationText, LanguageService.getTranslation(TranslationKey.LIVENESS_ACTION_SMILE_TEXT));
					break;
				case 'Frown':
					RenderingService.setText(animationContainer, '');
					LivenessStep.livenessAnimation = AnimationService.CreateAnimation(animationContainer, this.assetsFolder + '/animations/frown.json', true, true, {});
					RenderingService.setText(animationTitle, LanguageService.getTranslation(TranslationKey.LIVENESS_ACTION_FROWN_TITLE));
					RenderingService.setText(animationText, LanguageService.getTranslation(TranslationKey.LIVENESS_ACTION_FROWN_TEXT));
					break;
				case 'Tilt Left':
					RenderingService.setText(animationContainer, '');
					LivenessStep.livenessAnimation = AnimationService.CreateAnimation(animationContainer, this.assetsFolder + '/animations/look-left.json', true, true, {});
					RenderingService.setText(animationTitle, LanguageService.getTranslation(TranslationKey.LIVENESS_ACTION_LOOK_LEFT_TITLE));
					RenderingService.setText(animationText, LanguageService.getTranslation(TranslationKey.LIVENESS_ACTION_LOOK_LEFT_TEXT));
					break;
				case 'Tilt Right':
					RenderingService.setText(animationContainer, '');
					RenderingService.setText(animationTitle, LanguageService.getTranslation(TranslationKey.LIVENESS_ACTION_LOOK_RIGHT_TITLE));
					RenderingService.setText(animationText, LanguageService.getTranslation(TranslationKey.LIVENESS_ACTION_LOOK_RIGHT_TEXT));
					LivenessStep.livenessAnimation = AnimationService.CreateAnimation(animationContainer, this.assetsFolder + '/animations/look-right.json', true, true, {});
					break;
				case 'Tilt Up':
					RenderingService.setText(animationContainer, '');
					RenderingService.setText(animationTitle, LanguageService.getTranslation(TranslationKey.LIVENESS_ACTION_LOOK_UP_TITLE));
					RenderingService.setText(animationText, LanguageService.getTranslation(TranslationKey.LIVENESS_ACTION_LOOK_UP_TEXT));
					LivenessStep.livenessAnimation = AnimationService.CreateAnimation(animationContainer, this.assetsFolder + '/animations/look-up.json', true, true, {});
					break;
				case 'Tilt Down':
					RenderingService.setText(animationContainer, '');
					RenderingService.setText(animationTitle, LanguageService.getTranslation(TranslationKey.LIVENESS_ACTION_LOOK_DOWN_TITLE));
					RenderingService.setText(animationText, LanguageService.getTranslation(TranslationKey.LIVENESS_ACTION_LOOK_DOWN_TEXT));
					LivenessStep.livenessAnimation = AnimationService.CreateAnimation(animationContainer, this.assetsFolder + '/animations/look-down.json', true, true, {});
					break;
				default:
					console.log("Unknown action requested for liveness: " + poseAction);
					break;
			}
		} catch (error) {
			console.log(error);
		}
	}

	private static parseResponseData = ({
		entryData,
		entryDateTime,
		journeyId,
		requiredAction,
		highLevelResult,
		currentResult,
		hasError,
		isFinished
	}: UploadResponse): Partial<IIdScanData> => ({
		journeyId,
		journeyReference: entryData.scanReference,
		documents: (entryData.documents || []).map((doc): IExtendedDocument => removeNullOrUndefined({
			expirationDate: doc.expirationDate,
			issueDate: doc.issueDate,
			issueNumber: doc.issueNumber,
			number: doc.number,
			type: doc.type,
			blurCheck: entryData.blurCheck === 'Good',
			glareCheck: entryData.blurCheck === 'Good',
			lowResolutionCheck: entryData.lowResolutionCheck === 'Good',
			scanReference: entryData.scanReference,
			scannerIp: entryData.scannerIp,
			dateTime: entryDateTime ? new Date(entryDateTime) : undefined
		})),
		requiredAction,
		highLevelResult,
		currentResult,
		hasError,
		isFinished
	})

}
